<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Plans</h1>
        <p class="console-page__placeholder">
          Review and decide queued execution plans before work starts.
        </p>
        <ConsolePollingStatus
          active
          :busy="refreshBusy"
          idle-label="Auto-refresh every 30s"
          busy-label="Refreshing plans..."
          tone="active"
        />
      </div>

      <div class="console-page__actions">
        <button type="button" class="button button--ghost" :disabled="loading" @click="loadData({ showLoading: true })">
          Refresh
        </button>
      </div>
    </div>

    <section class="console-panel">
      <div class="console-signal-strip plans-summary-strip">
        <div class="console-signal" :class="pendingPlans.length > 0 ? 'console-signal--attention' : 'console-signal--ready'">
          <span class="console-signal__label">Pending Approval</span>
          <span class="console-signal__value">{{ pendingPlans.length }}</span>
          <span class="console-signal__note">Plans waiting for an operator decision.</span>
        </div>
        <div class="console-signal" :class="distinctJobCount > 0 ? 'console-signal--active' : 'console-signal--pending'">
          <span class="console-signal__label">Distinct Runs</span>
          <span class="console-signal__value">{{ distinctJobCount }}</span>
          <span class="console-signal__note">Runs currently represented in the inbox.</span>
        </div>
        <div class="console-signal" :class="oldestPlanLabel === 'none' ? 'console-signal--ready' : 'console-signal--pending'">
          <span class="console-signal__label">Oldest Plan</span>
          <span class="console-signal__value plans-summary-strip__value">{{ oldestPlanLabel }}</span>
          <span class="console-signal__note">Age is derived from the related run timestamp when available.</span>
        </div>
      </div>

      <ConsoleListState
        :loading="loading"
        :error="error"
        :is-empty="pendingPlans.length === 0"
        loading-message="Loading plans..."
        empty-message="No plans are waiting for approval."
      >
        <div class="console-filters plans-filters">
          <label class="console-filter">
            <span class="console-filter__label">Older than</span>
            <select v-model.number="ageFilterDays" class="form-input" :disabled="bulkSubmitting">
              <option v-for="option in ageFilterOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>

          <label class="console-filter">
            <span class="console-filter__label">Sort</span>
            <select v-model="sortOrder" class="form-input" :disabled="bulkSubmitting">
              <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>

        <div class="console-results plans-results">
          <label class="plans-select-all">
            <input
              type="checkbox"
              :checked="allVisibleSelected"
              :disabled="bulkSubmitting || visiblePlans.length === 0"
              aria-label="Select all visible plans"
              @change="toggleSelectAll"
            />
            <span>Select all</span>
          </label>
          <p class="console-results__count">
            <template v-if="ageFilterDays > 0">
              Showing {{ visiblePlans.length }} of {{ pendingPlans.length }} pending
              {{ pendingPlans.length === 1 ? 'plan' : 'plans' }}
            </template>
            <template v-else>
              {{ pendingPlans.length }} pending {{ pendingPlans.length === 1 ? 'plan' : 'plans' }}
            </template>
          </p>
        </div>

        <div v-if="selectedCount > 0" class="plans-bulk-bar">
          <div class="plans-bulk-bar__summary">
            <span class="console-badge console-badge--small console-badge--active">{{ selectedCount }} selected</span>
            <button type="button" class="button button--ghost" :disabled="bulkSubmitting" @click="clearSelection">
              Clear
            </button>
          </div>

          <div class="plans-bulk-bar__actions">
            <button
              type="button"
              class="button button--ghost button--danger"
              :disabled="bulkSubmitting"
              @click="toggleBulkReject"
            >
              {{ showBulkReject ? 'Cancel' : `Reject ${selectedCount} selected` }}
            </button>
          </div>
        </div>

        <div v-if="showBulkReject" class="plans-bulk-reject">
          <label class="console-filter">
            <span class="console-filter__label">Rejection Reason (applied to all {{ selectedCount }})</span>
            <input
              v-model="bulkRejectReason"
              type="text"
              class="form-input"
              list="plans-bulk-reject-reasons"
              placeholder="Why are these plans being rejected?"
            />
            <datalist id="plans-bulk-reject-reasons">
              <option v-for="reasonOption in reasonOptions" :key="reasonOption" :value="reasonOption" />
            </datalist>
          </label>
          <button
            type="button"
            class="button button--primary plans-bulk-reject__confirm"
            :disabled="bulkSubmitting || selectedCount === 0"
            @click="confirmBulkReject"
          >
            {{ bulkSubmitting ? 'Rejecting...' : `Confirm Reject ${selectedCount} ${selectedCount === 1 ? 'Plan' : 'Plans'}` }}
          </button>
        </div>

        <div
          v-if="bulkResult"
          class="alert plans-bulk-result"
          :class="`alert--${bulkResult.tone}`"
        >
          <p class="plans-bulk-result__message">{{ bulkResult.message }}</p>
          <ul v-if="bulkResult.failures.length" class="plans-bulk-result__failures">
            <li v-for="failure in bulkResult.failures" :key="failure.planId">
              {{ truncateId(failure.planId) }}: {{ failure.error }}
            </li>
          </ul>
        </div>

        <p v-if="visiblePlans.length === 0" class="console-results__count plans-empty-filter">
          No plans are older than the selected age filter.
        </p>

        <div class="console-stream">
          <div
            v-for="plan in visiblePlans"
            :key="plan.planId"
            class="console-stream__item plans-list__row"
            :class="{ 'plans-list__row--selected': selectedPlanIds.has(plan.planId) }"
          >
            <label class="plans-list__select" @click.stop>
              <input
                type="checkbox"
                :checked="selectedPlanIds.has(plan.planId)"
                :aria-label="`Select plan ${plan.title}`"
                @change="toggleSelection(plan.planId)"
              />
            </label>

            <router-link
              :to="{ name: 'ConsolePlanDetail', params: { id: plan.planId } }"
              class="plans-list__link"
            >
              <div>
                <h2 class="console-stream__title">{{ plan.title }}</h2>
                <p class="console-stream__meta">
                  {{ plan.proposedBeadCount }} proposed {{ plan.proposedBeadCount === 1 ? 'step' : 'steps' }}
                  <span v-if="plan.workspaceId"> / workspace {{ plan.workspaceId }}</span>
                  <span v-if="plan.jobCreatedAt"> / {{ formatRelativeTime(plan.jobCreatedAt) }}</span>
                </p>
              </div>

              <div class="console-stream__aside">
                <span class="console-badge console-badge--small console-badge--pending">
                  pending approval
                </span>
                <span class="plans-list__mono">{{ truncateId(plan.jobId) }}</span>
              </div>
            </router-link>
          </div>
        </div>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import ConsoleListState from '@/components/ConsoleListState.vue'
import ConsolePollingStatus from '@/components/ConsolePollingStatus.vue'
import { decodePlanId } from '@/composables/usePlanId.js'
import { summarizeJobTitle } from '@/composables/useJobPresentation.js'
import { formatRelativeTime } from '@/composables/useRelativeTime.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { useRejectionReasons } from '@/composables/useRejectionReasons.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { fetchAllPlanSummaryItems } from '@/services/planSummary.js'
import { bulkRejectPlans } from '@/services/planBulkActions.js'
import {
  PLAN_AGE_FILTER_DAYS,
  PLAN_SORT_ORDERS,
  filterAndSortPendingPlans,
} from '@/services/planListFilters.js'

const { request, requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsolePlans]')
const { presetReasons, recentReasons, recordReason } = useRejectionReasons()
const REFRESH_INTERVAL_MS = 30000

const loading = ref(true)
const error = ref('')
const plans = ref([])
const jobsById = ref(new Map())
const refreshBusy = ref(false)
let refreshInterval = null

const ageFilterDays = ref(0)
const sortOrder = ref('newest')

// Render the filter/sort dropdowns from the single source of truth in
// planListFilters.js so the option values can never drift from the values the
// filter/sort logic actually accepts.
const ageFilterOptions = PLAN_AGE_FILTER_DAYS.map((days) => ({
  value: days,
  label: days === 0 ? 'Any age' : `${days} day${days === 1 ? '' : 's'}`,
}))
const sortOptions = PLAN_SORT_ORDERS.map((order) => ({
  value: order,
  label: order === 'oldest' ? 'Oldest first' : 'Newest first',
}))

const selectedPlanIds = ref(new Set())
const showBulkReject = ref(false)
const bulkRejectReason = ref('')
const bulkSubmitting = ref(false)
const bulkResult = ref(null)

const pendingPlans = computed(() => {
  return plans.value
    .filter((plan) => plan.status === 'pending_approval')
    .map((plan) => {
      const decoded = decodePlanId(plan.planId) ?? {}
      const jobId = plan.jobId || decoded.jobId || ''
      const workspaceId = plan.workspaceId || decoded.workspaceId || ''
      const job = jobsById.value.get(jobId) ?? null
      const titleSource = job?.description || `Plan ${truncateId(jobId || plan.planId)}`

      return {
        ...plan,
        workspaceId,
        jobId,
        jobCreatedAt: job?.createdAt || plan.createdAt || '',
        // Count derives from the list payload's proposedBeads array. The
        // per-row /jobs/{jobId} response is unreliable for this while the
        // plan is in pending_approval because executable beads are not
        // created until after approval.
        proposedBeadCount: plan.proposedBeads?.length ?? 0,
        title: summarizeJobTitle(titleSource, truncateId(jobId || plan.planId), 72),
      }
    })
})

// Age-filtered + sorted view driven by the filter controls. Sorting lives here
// (not in pendingPlans) so the summary strip stays keyed to the full backlog.
const visiblePlans = computed(() => {
  return filterAndSortPendingPlans(pendingPlans.value, {
    olderThanDays: ageFilterDays.value,
    sortOrder: sortOrder.value,
  })
})

const distinctJobCount = computed(() => {
  return new Set(pendingPlans.value.map((plan) => plan.jobId).filter(Boolean)).size
})

const oldestPlanLabel = computed(() => {
  const timestamps = pendingPlans.value
    .map((plan) => plan.jobCreatedAt)
    .filter(Boolean)
    .sort()

  if (!timestamps.length) return 'none'
  return formatRelativeTime(timestamps[0])
})

// Only plans that are BOTH selected AND currently visible are actionable — a
// plan filtered out of view must never be swept into a bulk reject.
const selectedVisiblePlanIds = computed(() => {
  return visiblePlans.value
    .map((plan) => plan.planId)
    .filter((planId) => selectedPlanIds.value.has(planId))
})

const selectedCount = computed(() => selectedVisiblePlanIds.value.length)

const allVisibleSelected = computed(() => {
  return visiblePlans.value.length > 0
    && visiblePlans.value.every((plan) => selectedPlanIds.value.has(plan.planId))
})

const reasonOptions = computed(() => {
  const recent = recentReasons.value
  const presets = presetReasons.filter((preset) => !recent.includes(preset))
  return [...recent, ...presets]
})

function truncateId(value) {
  if (!value) return ''
  return value.length > 12 ? `${value.slice(0, 12)}...` : value
}

function toggleSelection(planId) {
  const next = new Set(selectedPlanIds.value)
  if (next.has(planId)) {
    next.delete(planId)
  } else {
    next.add(planId)
  }
  selectedPlanIds.value = next
}

function toggleSelectAll() {
  if (allVisibleSelected.value) {
    selectedPlanIds.value = new Set()
  } else {
    selectedPlanIds.value = new Set(visiblePlans.value.map((plan) => plan.planId))
  }
}

function clearSelection() {
  selectedPlanIds.value = new Set()
  showBulkReject.value = false
}

function toggleBulkReject() {
  showBulkReject.value = !showBulkReject.value
  if (showBulkReject.value) {
    bulkResult.value = null
  }
}

async function confirmBulkReject() {
  // Re-entrancy guard: the confirm button is :disabled while submitting, but
  // that is async — a fast double-click or Enter can invoke this twice before
  // the disabled state flushes, firing duplicate /decide calls and letting the
  // second run's bulkResult overwrite the first's true summary. Bail early.
  if (bulkSubmitting.value) return

  const ids = [...selectedVisiblePlanIds.value]
  if (!ids.length) return

  bulkSubmitting.value = true
  bulkResult.value = null

  try {
    const { succeeded, failed } = await bulkRejectPlans(request, ids, { reason: bulkRejectReason.value })

    if (succeeded.length && bulkRejectReason.value.trim()) {
      recordReason(bulkRejectReason.value)
    }

    if (failed.length === 0) {
      bulkResult.value = {
        tone: 'success',
        message: `Rejected ${succeeded.length} ${succeeded.length === 1 ? 'plan' : 'plans'}.`,
        failures: [],
      }
    } else if (succeeded.length === 0) {
      bulkResult.value = {
        tone: 'error',
        message: `Failed to reject ${failed.length} ${failed.length === 1 ? 'plan' : 'plans'}.`,
        failures: failed,
      }
    } else {
      bulkResult.value = {
        tone: 'warning',
        message: `Rejected ${succeeded.length} of ${ids.length} plans. ${failed.length} failed.`,
        failures: failed,
      }
    }

    // Drop the plans that were rejected from the selection; keep any that
    // failed so the operator can retry them without re-selecting.
    const failedIds = new Set(failed.map((failure) => failure.planId))
    selectedPlanIds.value = new Set([...selectedPlanIds.value].filter((planId) => failedIds.has(planId)))

    // Only collapse the panel and clear the typed reason on a clean sweep.
    // When some plans failed and stay selected for retry, preserve the reason
    // and keep the panel open so the operator can retry without re-typing.
    if (failed.length === 0) {
      showBulkReject.value = false
      bulkRejectReason.value = ''
    }

    // Refresh the inbox in place — the operator stays on the Plans list.
    await loadData({ background: true })
  } catch (bulkError) {
    // bulkRejectPlans aggregates per-plan failures and does not throw, but a
    // caller-side fault (e.g. loadData) must still surface, never swallow.
    bulkResult.value = {
      tone: 'error',
      message: bulkError.message || 'Bulk reject failed.',
      failures: [],
    }
    logger.warn('Bulk reject failed', bulkError)
  } finally {
    bulkSubmitting.value = false
  }
}

async function fetchPlans() {
  plans.value = await fetchAllPlanSummaryItems(requestJson, {
    status: 'pending_approval',
  })
}

// Retained to populate the row's title (job.description) and timestamp
// (job.createdAt). It does NOT feed the proposed-step count — that comes
// from the list payload's plan.proposedBeads array.
async function fetchJobs(planItems) {
  const jobIds = [...new Set(planItems.map((plan) => plan.jobId).filter(Boolean))]
  if (!jobIds.length) {
    jobsById.value = new Map()
    return
  }

  const results = await Promise.allSettled(jobIds.map(async (jobId) => {
    const job = await requestJson(`/jobs/${jobId}`)
    return [jobId, job]
  }))

  jobsById.value = new Map(
    results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value),
  )

  const rejected = results.filter((result) => result.status === 'rejected')
  if (rejected.length) {
    logger.warn('Failed to fetch some run context for plans inbox', rejected[0].reason)
  }
}

async function loadData({ showLoading = false, background = false } = {}) {
  if (showLoading) loading.value = true
  if (background) refreshBusy.value = true
  if (!background) error.value = ''

  try {
    await fetchPlans()
  } catch (plansError) {
    if (!background) {
      plans.value = []
      error.value = 'Failed to load plans.'
    }
    logger.warn('Failed to fetch plans inbox', plansError)
    loading.value = false
    refreshBusy.value = false
    return
  }
  error.value = ''

  try {
    await fetchJobs(plans.value)
  } catch (jobsError) {
    if (!background) {
      jobsById.value = new Map()
    }
    logger.warn('Failed to fetch runs for plans inbox', jobsError)
  }

  loading.value = false
  refreshBusy.value = false
}

function startRefreshInterval() {
  if (refreshInterval) clearInterval(refreshInterval)
  refreshInterval = setInterval(() => {
    void loadData({ background: true })
  }, REFRESH_INTERVAL_MS)
}

// When the visible set changes (filter change, background refresh, or a bulk
// reject), prune the selection to plans still on screen so a hidden plan can
// never remain selected — and thus never be acted on unseen.
watch(visiblePlans, (nextVisible) => {
  const visibleIds = new Set(nextVisible.map((plan) => plan.planId))
  const pruned = new Set([...selectedPlanIds.value].filter((planId) => visibleIds.has(planId)))
  if (pruned.size !== selectedPlanIds.value.size) {
    selectedPlanIds.value = pruned
  }
  if (pruned.size === 0) {
    showBulkReject.value = false
  }
})

onMounted(() => {
  loadData({ showLoading: true })
  startRefreshInterval()
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
})
</script>

<style scoped>
.plans-summary-strip {
  margin-bottom: 16px;
}

.plans-summary-strip__value {
  font-size: clamp(1.15rem, 2.3vw, 1.5rem);
  line-height: 1.05;
}

.plans-filters {
  grid-template-columns: repeat(2, minmax(180px, 0.5fr));
}

.plans-results {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.plans-select-all {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.84rem;
  color: var(--shell-text-muted);
  cursor: pointer;
}

.plans-bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border: 1px solid var(--shell-border);
  border-radius: 8px;
  background: var(--shell-surface-strong);
}

.plans-bulk-bar__summary,
.plans-bulk-bar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.plans-bulk-reject {
  display: grid;
  gap: 12px;
  margin-bottom: 12px;
  padding: 14px 16px;
  border: 1px solid var(--shell-border);
  border-radius: 8px;
  background: var(--shell-surface-strong);
}

.plans-bulk-reject__confirm {
  justify-self: start;
}

.plans-bulk-result {
  margin-bottom: 12px;
}

.plans-bulk-result__message {
  margin: 0;
}

.plans-bulk-result__failures {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 0.82rem;
}

.plans-empty-filter {
  padding: 8px 0;
}

.plans-list__row {
  align-items: center;
  gap: 12px;
}

.plans-list__row--selected {
  background: var(--shell-surface-strong);
}

.plans-list__select {
  display: inline-flex;
  align-items: center;
  padding-top: 2px;
}

.plans-list__link {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  color: inherit;
  text-decoration: none;
}

.plans-list__link:hover .console-stream__title {
  color: var(--shell-accent);
}

.plans-list__mono {
  font-family: var(--shell-mono-font);
  font-size: 0.74rem;
  color: var(--shell-text-muted);
}

@media (max-width: 768px) {
  .plans-filters {
    grid-template-columns: 1fr;
  }

  .plans-results {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
